/*
Modal component for displaying bulk publication queue progress
Shows real-time status of each vehicle being published
Autor: Assistant
Fecha: 2025-06-24
*/
import React from 'react'
import { Dialog, Flex, Text, Progress, Badge, Button, ScrollArea } from '@radix-ui/themes'
import { CheckIcon, CrossCircledIcon, UpdateIcon } from '@radix-ui/react-icons'

import { QueueItem, PublishQueueState } from '../hooks/usePublishQueue'

interface PublishQueueModalProps {
  isOpen: boolean
  onClose: () => void
  queueState: PublishQueueState
  onCancel?: () => void
  // NEW: Auto-close functionality props
  onCancelAutoClose?: () => void
  onAutoCloseComplete?: () => void
  // NEW: Operation type to distinguish between publish and unpublish
  operation?: 'publish' | 'unpublish'
}

const PublishQueueModal: React.FC<PublishQueueModalProps> = ({
  isOpen,
  onClose,
  queueState,
  onCancel,
  onCancelAutoClose,
  onAutoCloseComplete,
  operation = 'publish',
}) => {
  // NEW: Handle auto-close trigger
  React.useEffect(() => {
    if (queueState.completionState === 'auto-closing' && onAutoCloseComplete) {
      onAutoCloseComplete()
    }
  }, [queueState.completionState, onAutoCloseComplete])

  // NEW: Get terminology based on operation type
  const getOperationText = () => {
    return operation === 'unpublish' ? 'Despublicación' : 'Publicación'
  }

  const getOperationVerb = () => {
    return operation === 'unpublish' ? 'despublicados' : 'publicados'
  }

  const getOperationVerbGerund = () => {
    return operation === 'unpublish' ? 'Despublicando' : 'Publicando'
  }

  // NEW: Get completion message based on results
  const getCompletionMessage = () => {
    const total = queueState.totalItems
    const successful = queueState.completedCount
    const failed = queueState.failedCount
    const verb = getOperationVerb()

    if (failed === 0) {
      return `¡Todos los ${total} vehículos fueron ${verb} exitosamente!`
    } else if (successful === 0) {
      return `Falló la ${getOperationText().toLowerCase()} de todos los ${total} vehículos.`
    } else {
      return `Se ${verb} ${successful} de ${total} vehículos exitosamente. ${failed} fallaron.`
    }
  }

  const getStatusIcon = (status: QueueItem['status']) => {
    switch (status) {
      case 'completed':
        return <CheckIcon className="w-4 h-4 text-green-600" />
      case 'failed':
        return <CrossCircledIcon className="w-4 h-4 text-red-600" />
      case 'publishing':
        return <UpdateIcon className="w-4 h-4 text-blue-600 animate-spin" />
      default:
        return <div className="w-4 h-4 rounded-full bg-gray-300" />
    }
  }

  const getStatusBadge = (status: QueueItem['status']) => {
    switch (status) {
      case 'completed':
        return <Badge color="green">Completado</Badge>
      case 'failed':
        return <Badge color="red">Fallido</Badge>
      case 'publishing':
        return <Badge color="blue">{getOperationVerbGerund()}...</Badge>
      default:
        return <Badge color="gray">Pendiente</Badge>
    }
  }

  const progressPercentage =
    queueState.totalItems > 0
      ? Math.round(((queueState.completedCount + queueState.failedCount) / queueState.totalItems) * 100)
      : 0

  const canClose =
    !queueState.isProcessing || queueState.completedCount + queueState.failedCount === queueState.totalItems

  return (
    <Dialog.Root open={isOpen} onOpenChange={canClose ? onClose : undefined}>
      <Dialog.Content style={{ maxWidth: 600, maxHeight: '80vh' }}>
        <Dialog.Title>{getOperationText()} Masiva</Dialog.Title>

        <Flex direction="column" gap="4" mt="4">
          {/* Progress Summary */}
          <Flex direction="column" gap="2">
            <Flex justify="between" align="center">
              <Text size="3" weight="medium">
                Progreso General
              </Text>
              <Text size="2" color="gray">
                {queueState.completedCount + queueState.failedCount} de {queueState.totalItems}
              </Text>
            </Flex>
            <Progress value={progressPercentage} />
            <Flex gap="4" justify="center">
              <Flex align="center" gap="1">
                <CheckIcon className="w-4 h-4 text-green-600" />
                <Text size="2">Exitosos: {queueState.completedCount}</Text>
              </Flex>
              <Flex align="center" gap="1">
                <CrossCircledIcon className="w-4 h-4 text-red-600" />
                <Text size="2">Fallidos: {queueState.failedCount}</Text>
              </Flex>
            </Flex>
          </Flex>

          {/* Vehicle List */}
          <ScrollArea style={{ height: 300 }}>
            <Flex direction="column" gap="2">
              {queueState.items.map((item, index) => (
                <Flex
                  key={item.vehicleId}
                  align="center"
                  justify="between"
                  p="3"
                  className={`border rounded-md ${
                    queueState.currentIndex === index && queueState.isProcessing
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <Flex align="center" gap="3">
                    {getStatusIcon(item.status)}
                    <Flex direction="column" gap="1">
                      <Text size="2" weight="medium">
                        {item.vehicle.brand} {item.vehicle.model} {item.vehicle.version}
                      </Text>
                      <Text size="1" color="gray">
                        Patente: {item.vehicle.plate} | Año: {item.vehicle.year}
                      </Text>
                      {item.error && (
                        <Text size="1" color="red">
                          Error: {item.error}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                  <Flex align="center" gap="2">
                    {getStatusBadge(item.status)}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </ScrollArea>

          {/* Action Buttons */}
          <Flex gap="3" justify="end" pt="3">
            {queueState.isProcessing && canClose && (
              <Button variant="outline" onClick={onCancel} disabled={!canClose}>
                Cancelar
              </Button>
            )}
            <Button onClick={onClose} disabled={!canClose} variant={canClose ? 'solid' : 'outline'}>
              {canClose ? 'Cerrar' : 'Procesando...'}
            </Button>
          </Flex>

          {queueState.isProcessing && (
            <Flex justify="center" pt="2">
              <Text size="2" color="blue">
                {getOperationVerbGerund()} vehículos... Por favor no cierre esta ventana.
              </Text>
            </Flex>
          )}

          {/* NEW: Auto-close countdown display */}
          {queueState.completionState === 'delaying' && queueState.canAutoClose && (
            <Flex
              direction="column"
              align="center"
              gap="3"
              pt="4"
              style={{
                backgroundColor: '#f0f9ff',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #0ea5e9',
              }}
            >
              <Text size="3" weight="medium" color="blue">
                {getCompletionMessage()}
              </Text>
              <Text size="2" color="blue">
                Cerrando automáticamente en {queueState.autoCloseCountdown} segundo
                {queueState.autoCloseCountdown !== 1 ? 's' : ''}...
              </Text>
              <Button
                variant="outline"
                size="2"
                onClick={onCancelAutoClose}
                style={{ borderColor: '#0ea5e9', color: '#0ea5e9' }}
              >
                Cancelar cierre automático
              </Button>
            </Flex>
          )}

          {/* NEW: Completion message without auto-close */}
          {queueState.completionState === 'completed' && !queueState.canAutoClose && (
            <Flex
              justify="center"
              pt="4"
              style={{
                backgroundColor: '#f0fdf4',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #22c55e',
              }}
            >
              <Text size="3" weight="medium" color="green">
                {getCompletionMessage()}
              </Text>
            </Flex>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default PublishQueueModal
